import { useEffect, useRef, useState } from "react";
import { Loader2, FileSpreadsheet, Upload, CheckCircle2, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

const RULE_SETS = [["player_analysis", "Player Analysis"], ["player_breakdown", "Player Breakdown"]];

function MinAppsRule({ initial, initialNote }) {
  const [mins, setMins] = useState(initial);
  const [note, setNote] = useState(initialNote ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setMins(initial); setNote(initialNote ?? ""); }, [initial, initialNote]);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/braggers/stats/settings", { min_appearances: mins, note });
      setMins(data.min_appearances);
      setNote(data.note);
      toast.success("Player Stats settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the rule");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-5 pt-5 border-t border-[#E5E7EB]" data-testid="braggers-min-apps-rule">
      <label className="block mb-5">
        <span className="block text-sm font-semibold">Stats note</span>
        <span className="block text-xs text-[#777] mt-0.5 mb-2">Shown under the Player Stats heading on the public page and as the top line of each sheet in the downloaded spreadsheet. Leave it blank to hide it.</span>
        <input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} data-testid="braggers-stats-note-input"
          className="w-full rounded-md border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]" />
      </label>
      <p className="text-sm font-semibold">Gold highlight rule</p>
      <p className="text-xs text-[#777] mt-0.5">Players need at least this many appearances to be ranked for the gold top-3 highlights and player-card badges. 0 = no minimum. Kept when you upload a new file.</p>
      <div className="flex flex-wrap items-end gap-3 mt-3">
        {RULE_SETS.map(([k, label]) => (
          <label key={k} className="block">
            <span className="block text-xs font-medium text-[#555] mb-1">{label}: min. appearances</span>
            <input type="number" min="0" max="999" value={mins?.[k] ?? ""} data-testid={`braggers-min-apps-${k}`}
              onChange={(e) => setMins((m) => ({ ...m, [k]: e.target.value === "" ? "" : parseInt(e.target.value, 10) }))}
              className="w-44 rounded-md border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]" />
          </label>
        ))}
        <button type="button" onClick={save} disabled={saving || !mins} data-testid="braggers-min-apps-save"
          className="inline-flex items-center gap-2 border border-[#1E63C4] text-[#1E63C4] rounded-md px-4 py-2 text-sm font-semibold hover:bg-blue-50 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
        </button>
      </div>
    </div>
  );
}

export function BraggersStatsUpload() {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(null);
  const [result, setResult] = useState(null);

  const loadCurrent = () => api.get("/braggers/stats").then((r) => setCurrent(r.data)).catch(() => setCurrent({ datasets: [], failed: true }));
  useEffect(() => { loadCurrent(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Please choose an Excel .xlsx file");
      if (ref.current) ref.current.value = "";
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/braggers/stats/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success("Bragger's Cup data uploaded");
      loadCurrent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const sets = current?.datasets || [];
  return (
    <div className="bg-white rounded-lg border border-[#E5E7EB] p-6" data-testid="braggers-stats-upload">
      <div className="flex items-center gap-2 mb-1"><FileSpreadsheet className="h-4 w-4 text-[#1E63C4]" /><h3 className="font-semibold">Upload Braggers Cup Data</h3></div>
      <p className="text-sm text-[#777] mb-4">Upload the Excel (.xlsx) workbook with the <b>Player Analysis</b> and <b>Player Breakdown</b> tabs. A new upload completely replaces the existing data shown in the Player Stats section of the public page.</p>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="braggers-stats-upload-btn"
          className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-[#174e9c] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {busy ? "Uploading…" : "Choose Excel file"}
        </button>
        <input ref={ref} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={upload} className="hidden" data-testid="braggers-stats-file-input" />
        <span className="text-xs text-[#777]" data-testid="braggers-stats-current">
          {current === null
            ? "Loading current data…"
            : current.failed
            ? "Could not load the current data."
            : sets.length
            ? `Current: ${sets.map((s) => `${s.title} (${s.rows.length} rows)`).join(" · ")}${current.uploaded_at ? ` — uploaded ${fmt(current.uploaded_at)}` : ""}`
            : "No data uploaded yet — the public page shows “Data coming soon”."}
        </span>
      </div>
      {result && (
        <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-4" data-testid="braggers-stats-upload-result">
          <p className="text-sm font-semibold text-green-800 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Upload successful — {result.source_file}</p>
          <ul className="mt-2 text-sm text-green-900 space-y-0.5">
            {result.datasets.map((d) => (
              <li key={d.dataset} data-testid={`braggers-stats-result-${d.dataset}`}>{d.title}: <b>{d.rows}</b> rows, {d.columns} columns <span className="text-green-700">(tab “{d.sheet}”)</span></li>
            ))}
          </ul>
        </div>
      )}
      {current?.min_appearances && <MinAppsRule initial={current.min_appearances} initialNote={current.note} />}
    </div>
  );
}
